import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  getProjectInfo,
  getProjectList,
  createProject,
  updateProject,
  updateProjectUser,
  deleteProjectUser,
  getTaskList,
  getAllTasks,
  getProjectUsers,
} from 'api/projects/'
import { notifyError } from 'ui/components/Notify'
import { QUERY_STATUS } from 'utils/constants'

export const getProjectListAction = createAsyncThunk(
  'projects/getProjectList',
  async (flag) => {
    return await getProjectList(flag)
  }
)

export const createProjectAction = createAsyncThunk(
  'projects/createProject',
  async (data) => {
    return await createProject(data)
  }
)

export const updateProjectAction = createAsyncThunk(
  'projects/updateProject',
  async (data) => {
    return await updateProject(data.id, data.values)
  }
)

export const updateProjectUserAction = createAsyncThunk(
  'projects/updateProjectUser',
  async (data) => {
    return await updateProjectUser(data.slug, data.userId, data.roleId)
  }
)

export const deleteProjectUserAction = createAsyncThunk(
  'projects/deleteProjectUser',
  async (data) => {
    return await deleteProjectUser(data.slug, data.userId, data.roleId)
  }
)

export const getProjectInfoAction = createAsyncThunk(
  'projects/getProjectInfo',
  async (slug, { rejectWithValue }) => {
    const response = await getProjectInfo(slug)

    if (response?.errorMessage) {
      return rejectWithValue(response)
    }

    return response
  }
)
export const getProjectUsersAction = createAsyncThunk(
  'projects/getProjectUsers',
  async (slug) => {
    return await getProjectUsers(slug)
  }
)
export const getTaskListAction = createAsyncThunk(
  'projects/getTaskList',
  async (data) => {
    if (typeof data === 'string') {
      return await getTaskList(data, data, data.controller)
    } else return await getTaskList(data.slug, data, data.controller)
  }
)
export const getAllTasksAction = createAsyncThunk(
  'project/getAllTasks',
  async (slug) => {
    return await getAllTasks(slug)
  }
)

const projectsSlice = createSlice({
  name: 'projects',
  initialState: {
    getListStatus: QUERY_STATUS.IDLE,
    createStatus: QUERY_STATUS.IDLE,
    updateStatus: QUERY_STATUS.IDLE,
    updateUserStatus: QUERY_STATUS.IDLE,
    deleteUserStatus: QUERY_STATUS.IDLE,
    getInfoStatus: QUERY_STATUS.IDLE,
    list: [],
    taskList: [],
    sortedTask: [],
    allTasks: [],
    getTaskStatus: QUERY_STATUS.IDLE,
    getAllTasksStatus: QUERY_STATUS.IDLE,
    projectInfo: null,
    createProjectError: null,
  },
  reducers: {
    clearCreateStatus: (state) => {
      state.createStatus = QUERY_STATUS.IDLE
    },
    clearProjectInfo: (state) => {
      state.projectInfo = null
      state.getInfoStatus = QUERY_STATUS.IDLE
    },
    clearTaskList: (state) => {
      if (!state.projectInfo) {
        state.taskList = []
        state.sortedTask = []
      }
      state.allTasks = []
    },
    clearUpdateStatus: (state) => {
      state.updateStatus = QUERY_STATUS.IDLE
    },
    clearProjectList: (state) => {
      state.list = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProjectListAction.pending, (state) => {
        state.getListStatus = QUERY_STATUS.PENDING
      })
      .addCase(getProjectListAction.fulfilled, (state, action) => {
        state.list = action.payload || []
        state.getListStatus = QUERY_STATUS.IDLE
      })
    builder
      .addCase(createProjectAction.pending, (state) => {
        state.createStatus = QUERY_STATUS.PENDING
      })
      .addCase(createProjectAction.fulfilled, (state, action) => {
        if (action.payload?.errorMessage) {
          const errors = action.payload?.data?.errors || null
          state.createStatus = QUERY_STATUS.ERROR
          state.createProjectError = errors
          notifyError(Object.values(errors).join(', '))
        } else {
          state.createStatus = QUERY_STATUS.SUCCESS
        }
      })
    builder
      .addCase(updateProjectAction.pending, (state) => {
        state.updateStatus = QUERY_STATUS.PENDING
      })
      .addCase(updateProjectAction.fulfilled, (state, action) => {
        if (action.payload?.errorMessage) {
          const errors = action.payload?.data?.errors || null
          state.updateStatus = QUERY_STATUS.ERROR
          notifyError(
            errors
              ? Object.values(errors).join(', ')
              : action.payload?.data?.message
          )
        } else {
          state.updateStatus = QUERY_STATUS.SUCCESS
        }
      })
    builder
      .addCase(getTaskListAction.pending, (state) => {
        state.getTaskStatus = QUERY_STATUS.PENDING
      })
      .addCase(getTaskListAction.fulfilled, (state, action) => {
        state.getTaskStatus = QUERY_STATUS.SUCCESS
        state.allTasks = action.payload?.allTasks.data || []
        state.taskList = action.payload?.task.data || []
        state.sortedTask = action.payload?.sortedTask || []
      })
      .addCase(getTaskListAction.rejected, (state) => {
        state.getTaskStatus = QUERY_STATUS.ERROR
      })
      .addCase(getAllTasksAction.pending, (state) => {
        state.getAllTasksStatus = QUERY_STATUS.PENDING
      })
      .addCase(getAllTasksAction.rejected, (state) => {
        state.getAllTasksStatus = QUERY_STATUS.ERROR
      })
      .addCase(getAllTasksAction.fulfilled, (state, action) => {
        state.allTasks = action.payload.data || []
        state.getAllTasksStatus = QUERY_STATUS.SUCCESS
      })
    builder
      .addCase(getProjectInfoAction.pending, (state, action) => {
        if (state.projectInfo?.slug !== action.meta.arg) {
          state.projectInfo = null
        }
        state.getInfoStatus = QUERY_STATUS.PENDING
      })
      .addCase(getProjectInfoAction.rejected, (state) => {
        state.projectInfo = null
        state.getInfoStatus = QUERY_STATUS.ERROR
      })
      .addCase(getProjectInfoAction.fulfilled, (state, action) => {
        state.projectInfo = action.payload.data || null
        state.getInfoStatus = QUERY_STATUS.SUCCESS
      })
    builder
      .addCase(getProjectUsersAction.pending, (state) => {
        state.getProjectUsersStatus = QUERY_STATUS.PENDING
      })
      .addCase(getProjectUsersAction.fulfilled, (state, action) => {
        state.projectUsers = action.payload.data || null
        state.getProjectUsersStatus = QUERY_STATUS.SUCCESS
      })
    builder
      .addCase(updateProjectUserAction.pending, (state) => {
        state.updateUserStatus = QUERY_STATUS.PENDING
      })
      .addCase(updateProjectUserAction.fulfilled, (state) => {
        state.updateUserStatus = QUERY_STATUS.SUCCESS
      })
    builder
      .addCase(deleteProjectUserAction.pending, (state) => {
        state.deleteUserStatus = QUERY_STATUS.PENDING
      })
      .addCase(deleteProjectUserAction.fulfilled, (state) => {
        state.deleteUserStatus = QUERY_STATUS.SUCCESS
      })
  },
})

export const projectListSelector = (state) => state.projects.list
export const projectInfoStatusSelector = (state) => state.projects.getInfoStatus
export const projectInfoIsLoadedSelector = (state) =>
  projectInfoStatusSelector(state) == QUERY_STATUS.SUCCESS
export const projectInfoSelector = (state) => state.projects.projectInfo
export const createProjectStatusSelector = (state) =>
  state.projects.createStatus
export const updateProjectStatusSelector = (state) =>
  state.projects.updateStatus
export const createProjectErrorSelector = (state) =>
  state.projects.createProjectError
export const taskListSelector = (state) => state.projects.taskList
export const sortedTaskSelector = (state) => state.projects.sortedTask
export const taskStatusSelector = (state) => state.projects.getTaskStatus
export const allTasksSelector = (state) => state.projects.allTasks
export const projectListStatusSelector = (state) => state.projects.getListStatus
export const projectUsersSelector = (state) => state.projects.projectUsers
export const getProjectUsersStatusSelector = (state) =>
  state.projects.getProjectUsersStatus
export const projectUsersAreLoadedSelector = (state) =>
  getProjectUsersStatusSelector(state) == QUERY_STATUS.SUCCESS
export const getAllTasksStatusSelector = (state) =>
  state.projects.getAllTasksStatus
export const allTasksAreLoadedSelector = (state) =>
  getAllTasksStatusSelector(state) == QUERY_STATUS.SUCCESS
export const {
  clearCreateStatus,
  clearUpdateStatus,
  clearProjectInfo,
  clearTaskList,
  clearProjectList,
} = projectsSlice.actions

export default projectsSlice.reducer
